/** Seasonal-closure warnings — seasons.json's per-way DATES-OPEN windows,
 * fetched lazily per state and checked against a plan's snapped legs. Pure
 * date-window math plus a lazy-cached fetch facade; App.tsx's future wave
 * owns wiring closureWarnings' output into the UI and RouteOverlayView's
 * `closures` field. */

import { TILES, type ManifestLayer, type StateManifest } from "../catalog";
import type { LonLat, Plan } from "./types";

/** One seasonally-restricted way. `p` is [lon, lat] pairs (GeoJSON axis
 * order, same as everywhere else in the web app); `d` is the DATES-OPEN
 * window, "MM/DD-MM/DD". `id` matches PlanLeg.closureIds — see types.ts's
 * doc on that field for the DLR2 writer's +1/-1 convention. */
export interface SeasonEntry {
  id: number;
  n: string;
  d: string;
  p: LonLat[];
}

// Resolved per-region results, cached only on a SUCCESSFUL parse — see
// seasonsFor's doc for why a failure must never land here. In-flight
// promises live in a separate map so concurrent callers during the same
// region's first load share one fetch instead of firing one apiece; an
// entry is removed the moment its fetch settles, success or failure.
const resultCache = new Map<string, SeasonEntry[]>();
const inflight = new Map<string, Promise<SeasonEntry[]>>();

/** null on any failure (bad status, network error, non-JSON, JSON that
 * isn't an array) — the caller collapses that to [] itself, but keeping
 * null here lets seasonsFor tell "genuinely zero closures" apart from
 * "couldn't find out" so only the former gets cached. */
async function fetchSeasons(url: string): Promise<SeasonEntry[] | null> {
  try {
    const r = await fetch(url, { cache: "no-cache" });
    if (!r.ok) return null;
    const j = await r.json();
    return Array.isArray(j) ? (j as SeasonEntry[]) : null;
  } catch {
    return null;
  }
}

/** Lazy cached fetch of a state's seasonal-closure entries — mirrors
 * router.ts's planRouterFor lazy-open facade: null when the manifest
 * serves no seasons layer at all (nothing to ever warn about, so callers
 * can skip the feature entirely), otherwise a function that fetches the
 * region's seasons.json once and caches the parsed result by region. A
 * fetch/parse failure resolves to [] — a closure warning is a nice-to-
 * have, never worth crashing the planner over — but is deliberately NOT
 * cached, so a flaky network on first load doesn't silence warnings for
 * the rest of the session; the next call retries. */
export function seasonsFor(manifest: StateManifest): (() => Promise<SeasonEntry[]>) | null {
  const layer = manifest.layers.seasons as ManifestLayer | undefined;
  if (!layer) return null;

  const region = manifest.region;
  const url = `${TILES}/${region}/${layer.file}`;

  return async function load(): Promise<SeasonEntry[]> {
    const cached = resultCache.get(region);
    if (cached) return cached;
    const pending = inflight.get(region);
    if (pending) return pending;

    const p = fetchSeasons(url).then((entries) => {
      inflight.delete(region);
      if (entries !== null) resultCache.set(region, entries);
      return entries ?? [];
    });
    inflight.set(region, p);
    return p;
  };
}

export interface SeasonWindow {
  start: { m: number; day: number };
  end: { m: number; day: number };
}

function parseMonthDay(s: string): { m: number; day: number } | null {
  const match = /^(\d{1,2})\/(\d{1,2})$/.exec(s.trim());
  if (!match) return null;
  const m = Number(match[1]);
  const day = Number(match[2]);
  if (m < 1 || m > 12 || day < 1 || day > 31) return null;
  return { m, day };
}

/** "MM/DD-MM/DD" -> the window's start/end month-day pairs, or null for
 * anything that doesn't parse — a corrupt/hand-edited seasons.json entry
 * degrades to "no warning" (openNow below), never a crash. */
export function parseWindow(d: string): SeasonWindow | null {
  const parts = d.split("-");
  if (parts.length !== 2) return null;
  const start = parseMonthDay(parts[0]);
  const end = parseMonthDay(parts[1]);
  if (!start || !end) return null;
  return { start, end };
}

function compareMonthDay(a: { m: number; day: number }, b: { m: number; day: number }): number {
  return a.m !== b.m ? a.m - b.m : a.day - b.day;
}

/** Whether `d`'s window contains `today`'s (local) month/day, inclusive on
 * both bounds — null when `d` doesn't parse. A window that crosses the
 * year boundary (start > end, e.g. "10/15-05/30" — open through winter)
 * is open when today is on/after start OR on/before end, rather than the
 * plain AND a same-year window needs. */
export function openNow(d: string, today: Date): boolean | null {
  const window = parseWindow(d);
  if (!window) return null;

  const t = { m: today.getMonth() + 1, day: today.getDate() };
  const wraps = compareMonthDay(window.start, window.end) > 0;
  if (!wraps) {
    return compareMonthDay(t, window.start) >= 0 && compareMonthDay(t, window.end) <= 0;
  }
  return compareMonthDay(t, window.start) >= 0 || compareMonthDay(t, window.end) <= 0;
}

export interface ClosureWarning {
  id: number;
  name: string;
  window: string;
  openNow: boolean | null;
  coords: LonLat[];
}

/** Every seasonal closure this plan's SNAPPED legs traverse — the union of
 * PlanLeg.closureIds across those legs, mapped through `entries` by the
 * entry's own `id` field (not array position — seasons.json ids aren't
 * guaranteed contiguous or sorted), sorted by name for a stable, readable
 * list. An id with no matching entry — a stale closureId left over from
 * before a seasons.json republish — is skipped rather than surfaced as a
 * broken warning. */
export function closureWarnings(plan: Plan, entries: SeasonEntry[], today: Date): ClosureWarning[] {
  const byId = new Map(entries.map((e) => [e.id, e]));
  const ids = new Set<number>();
  for (const leg of plan.legs) {
    if (leg.mode !== "snapped") continue;
    for (const id of leg.closureIds ?? []) ids.add(id);
  }

  const warnings: ClosureWarning[] = [];
  for (const id of ids) {
    const entry = byId.get(id);
    if (!entry) continue;
    warnings.push({
      id: entry.id,
      name: entry.n,
      window: entry.d,
      openNow: openNow(entry.d, today),
      coords: entry.p,
    });
  }
  return warnings.sort((a, b) => a.name.localeCompare(b.name));
}
